import { useState } from 'react'
import { useSupabaseQuery, useSupabaseMutation } from '../../hooks/useSupabase'
import Modal from '../shared/Modal'

export default function ApiCredentialsTab({ selectedAccount }) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createdKey, setCreatedKey] = useState(null)
  const [deletingCred, setDeletingCred] = useState(null)
  const [copiedKey, setCopiedKey] = useState(null)

  const { data: credentials, loading, error, refetch } = useSupabaseQuery('EmailApiCredentials', {
    eq: { IsDeleted: false, ...(selectedAccount && { AccountId: selectedAccount.AccountId }) },
    order: { column: 'Name', ascending: true }
  })

  const { insert, softDelete, loading: mutating } = useSupabaseMutation('EmailApiCredentials')

  const handleCreate = async (name) => {
    const { data, error } = await insert({
      AccountId: selectedAccount.AccountId,
      Name: name,
      IsDeleted: false
    })
    if (!error && data) {
      setShowCreateModal(false)
      setCreatedKey(data)
      refetch()
    }
  }

  const handleDelete = async () => {
    const { error } = await softDelete(deletingCred.ApiCredentialId, 'ApiCredentialId')
    if (!error) {
      setDeletingCred(null)
      refetch()
    }
  }

  const copyToClipboard = (key) => {
    navigator.clipboard.writeText(key)
    setCopiedKey(key)
    setTimeout(() => setCopiedKey(null), 2000)
  }

  if (!selectedAccount) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Account Selected</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Please select an account from the Accounts tab first
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">API Credentials</h2>
          <p className="text-gray-600 dark:text-gray-400">
            API Keys for: {selectedAccount.Name}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>Generate New Key</span>
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex">
          <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="ml-3">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Use these API keys to authenticate requests to the Send-Email edge function. Include the key in the <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">X-Api-Key</code> header or <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">Authorization: Bearer</code> header.
            </p>
          </div>
        </div>
      </div>

      {/* API Keys List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {credentials?.map((cred) => (
            <li key={cred.ApiCredentialId} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {cred.Name}
                    </h3>
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-xs font-semibold rounded">
                      Active
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <code className="px-3 py-2 bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 rounded font-mono text-sm">
                      {cred.Key}
                    </code>
                    <button
                      onClick={() => copyToClipboard(cred.Key)}
                      className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition"
                      title="Copy to clipboard"
                    >
                      {copiedKey === cred.Key ? (
                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    ID: {cred.ApiCredentialId}
                  </p>
                </div>
                <button
                  onClick={() => setDeletingCred(cred)}
                  className="ml-4 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
                  title="Delete"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </li>
          ))}
        </ul>

        {(!credentials || credentials.length === 0) && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No API keys</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Generate your first API key to start sending emails.</p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <Modal title="Generate API Key" onClose={() => setShowCreateModal(false)} size="sm">
          <CreateKeyForm
            onSubmit={handleCreate}
            onCancel={() => setShowCreateModal(false)}
            loading={mutating}
          />
        </Modal>
      )}

      {/* Created Key Success Modal */}
      {createdKey && (
        <Modal title="API Key Created Successfully" onClose={() => setCreatedKey(null)}>
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex">
                <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <p className="ml-3 text-sm text-green-800 dark:text-green-200">
                  Your API key has been generated. Make sure to copy it now as you won't be able to see it again!
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                API Key
              </label>
              <div className="flex items-center space-x-2">
                <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 rounded font-mono text-sm break-all">
                  {createdKey.Key}
                </code>
                <button
                  onClick={() => copyToClipboard(createdKey.Key)}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition"
                >
                  {copiedKey === createdKey.Key ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setCreatedKey(null)}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deletingCred && (
        <Modal title="Delete API Key" onClose={() => setDeletingCred(null)} size="sm">
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">
              Are you sure you want to delete the API key <strong>{deletingCred.Name}</strong>?
              This action cannot be undone and will break any integrations using this key.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeletingCred(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={mutating}
                className="px-4 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {mutating ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

function CreateKeyForm({ onSubmit, onCancel, loading }) {
  const [name, setName] = useState('')

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(name)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Key Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="e.g., Production API Key"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Give this key a descriptive name to identify where it's used
        </p>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
        >
          {loading ? 'Generating...' : 'Generate Key'}
        </button>
      </div>
    </form>
  )
}
